// src/constants/roles.js
/**
 * 🏢 Enterprise Rollen-Logik für WireTrack Frontend
 * Zentrale Berechtigungsprüfungen
 * 
 * Rollen-Hierarchie (DB rollen.id):
 * ┌────────────────────────────────────────────────────────────────┐
 * │ ID │ Name              │ Power │ Mgmt │ Leitstand │ Admin-Only │
 * ├────────────────────────────────────────────────────────────────┤
 * │  1 │ Admin             │  ✅   │  ✅  │    ✅     │     ✅     │
 * │  2 │ Geschäftsleitung  │  ✅   │  ✅  │    ✅     │     ❌     │
 * │  3 │ Projektleitung    │  ✅   │  ❌  │    ✅     │     ❌     │  // alle Projekte/Services
 * │  4 │ Sekretariat       │  ✅   │  ✅  │    ✅     │     ❌     │
 * │  5 │ Mitarbeiter       │  ❌   │  ❌  │    ❌     │     ❌     │
 * │  6 │ Lehrling          │  ❌   │  ❌  │    ❌     │     ❌     │
 * └────────────────────────────────────────────────────────────────┘
 * 
 * Zugriffs-Gruppen:
 * - POWER_ROLES (1-4): Beliebige Einträge bearbeiten/löschen, Leitstand, unbegrenzte Stunden
 * - MGMT_ROLES (1,2,4): Admin-Seiten, Personal, Absenzen - PL (3) ausgenommen!
 * - ADMIN_ONLY (1): Systemkritische Operationen (z. B. Jahres-Rollover, Ferien-Sollregeln, Backup, 2FA-Reset)
 * 
 * @module client/constants/roles
 */

// 🏢 Enterprise: Rollen-IDs als Konstanten
export const ROLE_ADMIN = 1;
export const ROLE_GL = 2;
export const ROLE_PL = 3;
export const ROLE_SEK = 4;
export const ROLE_MITARBEITER = 5;
export const ROLE_LEHRLING = 6;

// 🏢 Enterprise: Rollen-Sets (frozen für Immutability)
export const POWER_ROLE_IDS = Object.freeze([1, 2, 3, 4]);
export const MGMT_ROLE_IDS = Object.freeze([1, 2, 4]); // OHNE PL!
export const ADMIN_ONLY_IDS = Object.freeze([1]);

// 🏢 Enterprise: Rollen-Namen für Anzeige
export const ROLE_NAMES = Object.freeze({
  [ROLE_ADMIN]: "Admin",
  [ROLE_GL]: "Geschäftsleitung",
  [ROLE_PL]: "Projektleitung",
  [ROLE_SEK]: "Sekretariat",
  [ROLE_MITARBEITER]: "Mitarbeiter",
  [ROLE_LEHRLING]: "Lehrling",
});

// 🏢 Enterprise: Kurzformen für Badge-Anzeige
export const ROLE_SHORT = Object.freeze({
  [ROLE_ADMIN]: "Admin",
  [ROLE_GL]: "GL",
  [ROLE_PL]: "PL",
  [ROLE_SEK]: "Sek",
  [ROLE_MITARBEITER]: "MA",
  [ROLE_LEHRLING]: "Lehrling",
});

/* ───────────────── Helper-Funktionen ───────────────── */

/** Normalizer: Umlaute + Groß/Kleinschreibung egal */
const norm = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase()
    .replaceAll("ä", "ae")
    .replaceAll("ö", "oe")
    .replaceAll("ü", "ue");

/**
 * 🏢 Enterprise: Extrahiert Rollen-ID aus User-Objekt (robuste Varianten-Prüfung)
 * @param {Object} user - User-Objekt
 * @returns {number} Rollen-ID oder 99 wenn nicht gefunden
 */
export function getRoleId(user) {
  if (!user) return 99;
  const rid = Number(user?.rolle_id ?? user?.role_id ?? user?.rolleId ?? user?.roleId ?? NaN);
  return Number.isFinite(rid) && rid >= 1 && rid <= 6 ? rid : 99;
}

/**
 * 🏢 Enterprise: Prüft ob User Power-User ist (Rollen 1-4)
 * @param {Object} user - User-Objekt
 * @returns {boolean}
 */
export function isPowerUser(user) {
  return POWER_ROLE_IDS.includes(getRoleId(user));
}

/**
 * 🏢 Enterprise: Prüft ob User Management-Zugriff hat (Admin, GL, Sek)
 * WICHTIG: Projektleitung (3) ist explizit AUSGESCHLOSSEN!
 * @param {Object} user - User-Objekt
 * @returns {boolean}
 */
export function canSeeMgmt(user) {
  if (!user) return false;

  const rid = getRoleId(user);
  if (MGMT_ROLE_IDS.includes(rid)) return true;

  // String-Rolle fallback (z. B. bei älteren Sessions)
  const rs = norm(user?.rolle ?? user?.rolle_key ?? user?.role ?? user?.role_key);
  if (!rs) return false;

  const allowed = new Set([
    "admin",
    "gl", "geschaeftsleitung", "geschaftsleitung", "geschäftsleitung",
    "sekretariat", "buero", "büro", "sek",
  ]);

  if (allowed.has(rs)) return true;
  if (rs.startsWith("geschaeftsleit") || rs.startsWith("geschaftsleit") || rs.startsWith("geschäftsleit")) return true;

  return false;
}

/**
 * 🏢 Enterprise: Prüft ob User Leitstand sehen darf (Rollen 1-4)
 * @param {Object} user - User-Objekt
 * @returns {boolean}
 */
export function canSeeLeitstand(user) {
  if (!user) return false;

  const rid = getRoleId(user);
  if (POWER_ROLE_IDS.includes(rid)) return true;

  // String-Fallback
  const rs = norm(user?.rolle ?? user?.rolle_key ?? user?.role ?? user?.role_key);
  if (!rs) return false;

  const allowed = new Set([
    "admin",
    "gl", "geschaeftsleitung", "geschaftsleitung", "geschäftsleitung",
    "pl", "projektleitung", "projektleiter",
    "sekretariat", "buero", "büro", "sek",
  ]);

  if (allowed.has(rs)) return true;
  if (rs.startsWith("projektleit")) return true;
  if (rs.startsWith("geschaeftsleit") || rs.startsWith("geschaftsleit") || rs.startsWith("geschäftsleit")) return true;

  return false;
}

/**
 * 🏢 Enterprise: Prüft ob User Stunden unbegrenzt zurückdatieren darf (Rollen 1-4)
 * Backend: timeEntryGuard PRIV_ROLES
 * @param {Object} user - User-Objekt
 * @returns {boolean}
 */
export function canEnterHoursUnlimited(user) {
  return isPowerUser(user);
}

/**
 * 🏢 Enterprise: Prüft ob User Admin ist (Rolle 1)
 * @param {Object} user - User-Objekt
 * @returns {boolean}
 */
export function isAdmin(user) {
  return getRoleId(user) === ROLE_ADMIN;
}

/** Personal/HR: Firmenkalender (Feiertage, Vorholtage) — Admin, GL, Sekretariat */
export function canHrEditFirmenkalender(user) {
  return canSeeMgmt(user);
}

/** Ferien-Sollregeln (Alter/Lehrjahr) — nur Admin */
export function canHrEditFerienregeln(user) {
  return isAdmin(user);
}

/** Jahres-/Lehrlings-Rollover — nur Admin */
export function canHrRunRollover(user) {
  return isAdmin(user);
}

/** SUVA / GL-Kontrollen lesen: Admin, GL, Sek */
export function canReadSuvaKontrollen(user) {
  const rid = getRoleId(user);
  return rid === ROLE_ADMIN || rid === ROLE_GL || rid === ROLE_SEK;
}

/** SUVA / GL-Kontrollen schreiben: nur Admin, GL */
export function canWriteSuvaKontrollen(user) {
  const rid = getRoleId(user);
  return rid === ROLE_ADMIN || rid === ROLE_GL;
}

/** Admin oder GL (ohne Sekretariat) — z. B. Offerte-Flow-Config */
export function canConfigureOfferteFlow(user) {
  return canWriteSuvaKontrollen(user);
}

/**
 * 🏢 Enterprise: Prüft ob User beliebige Einträge bearbeiten darf
 * @param {Object} user - User-Objekt
 * @returns {boolean}
 */
export function canEditAny(user) {
  return isPowerUser(user);
}

/**
 * 🏢 Enterprise: Prüft ob User beliebige Einträge löschen darf
 * @param {Object} user - User-Objekt
 * @returns {boolean}
 */
export function canDeleteAny(user) {
  return isPowerUser(user);
}

/**
 * 🏢 Enterprise: Prüft ob User einen Eintrag bearbeiten darf
 * @param {Object} user - User-Objekt
 * @param {number} ownerUserId - User-ID des Eintrag-Besitzers
 * @returns {boolean}
 */
export function canEdit(user, ownerUserId) {
  if (canEditAny(user)) return true;
  const userId = Number(user?.id ?? 0);
  return userId > 0 && userId === Number(ownerUserId);
}

/**
 * 🏢 Enterprise: Prüft ob User einen Eintrag löschen darf
 * @param {Object} user - User-Objekt
 * @param {number} ownerUserId - User-ID des Eintrag-Besitzers
 * @returns {boolean}
 */
export function canDelete(user, ownerUserId) {
  if (canDeleteAny(user)) return true;
  const userId = Number(user?.id ?? 0);
  return userId > 0 && userId === Number(ownerUserId);
}

/**
 * Rollen 5/6 (Mitarbeiter/Lehrling)
 * @param {Object} user
 * @returns {boolean}
 */
export function isRestrictedStaff(user) {
  const rid = getRoleId(user);
  return rid === ROLE_MITARBEITER || rid === ROLE_LEHRLING;
}

export function isLehrling(user) {
  return getRoleId(user) === ROLE_LEHRLING;
}

export function isMitarbeiter(user) {
  return getRoleId(user) === ROLE_MITARBEITER;
}

/** Stunden-Picker: Admin/GL/Büro, PL, Mitarbeiter (nicht Lehrling) */
export function canPickHoursTarget(user) {
  const rid = getRoleId(user);
  return rid === ROLE_ADMIN || rid === ROLE_GL || rid === ROLE_SEK || rid === ROLE_PL || rid === ROLE_MITARBEITER;
}

export function canAccessOffers(user) {
  if (!user) return false;
  return !isRestrictedStaff(user);
}

/** Kunden/Objekte einsehen (Listen, Details, Dateien) — Rollen 1–6 */
export function canAccessCustomers(user) {
  if (!user) return false;
  const rid = getRoleId(user);
  return rid >= 1 && rid <= 6;
}

/** Kunden/Objekte verwalten (anlegen, bearbeiten, löschen) — nur Power-Rollen 1–4 */
export function canManageCustomers(user) {
  return isPowerUser(user);
}

export function canAccessInventory(user) {
  if (!user) return false;
  return !isRestrictedStaff(user);
}

export function canAccessArchive(user) {
  if (!user) return false;
  return !isRestrictedStaff(user);
}

export function canAccessWartung(user) {
  if (!user) return false;
  return !isRestrictedStaff(user);
}

/** Mängelbericht fachlich unterschreiben (Dropdown + Code) — Admin, PL und GL */
export function canSignMaengelBericht(user) {
  const rid = getRoleId(user);
  return rid === ROLE_ADMIN || rid === ROLE_GL || rid === ROLE_PL;
}

/**
 * 🏢 Enterprise: Schönschreiber für Rollen-Namen (für Badges etc.)
 * @param {string|number} raw - Rollen-ID oder -Name
 * @returns {string} Formatierter Rollen-Name
 */
export function prettyRole(raw) {
  // Numerisch?
  const num = Number(raw);
  if (Number.isFinite(num) && ROLE_NAMES[num]) {
    return ROLE_NAMES[num];
  }

  // String-Lookup
  const s = norm(raw);
  const map = {
    admin: "Admin",
    gl: "Geschäftsleitung",
    geschaeftsleitung: "Geschäftsleitung",
    geschaftsleitung: "Geschäftsleitung",
    "geschäftsleitung": "Geschäftsleitung",
    pl: "Projektleitung",
    projektleitung: "Projektleitung",
    projektleiter: "Projektleitung",
    sek: "Sekretariat",
    sekretariat: "Sekretariat",
    buero: "Sekretariat",
    "büro": "Sekretariat",
    mitarbeiter: "Mitarbeiter",
    lehrling: "Lehrling",
  };
  return map[s] || (raw ? String(raw).charAt(0).toUpperCase() + String(raw).slice(1) : "");
}

/**
 * 🏢 Enterprise: Kurzform für Badge-Anzeige
 * @param {string|number} raw - Rollen-ID oder -Name
 * @returns {string} Kurzform
 */
export function shortRole(raw) {
  const num = Number(raw);
  if (Number.isFinite(num) && ROLE_SHORT[num]) {
    return ROLE_SHORT[num];
  }
  return prettyRole(raw);
}
